import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Account } from '../../../../../services/account'
import {
  Person,
  listPeople as apiListPeople
} from '../../../../../services/person'
import {
  Group,
  listGroups as apiListGroups
} from '../../../../../services/group'
import {
  Category,
  listCategories as apiListCategories
} from '../../../../../services/category'
import { AccountFormatted } from '../../../utils/formatAccount'
import { DefaultLoader } from '../../../../../components/DefaultLoader'

import { useAccount, usePerson, useGroup } from './hooks'

interface AccountFormContextContent {
  account?: AccountFormatted
  person?: Person
  group?: Group
  loading: boolean
  getAccountState: () => AccountFormatted
  updateAccountState: (account: Account) => AccountFormatted
  setLoading: (value: boolean) => void
  people: Person[]
  loadingPeople: boolean
  listPeople: (search: string) => void
  groups: Group[]
  loadingGroups: boolean
  listGroups: (search: string) => void
  categories: Category[]
  loadingCategories: boolean
  listCategories: () => void
}

export const AccountFormContext =
  React.createContext<AccountFormContextContent>(
    {} as AccountFormContextContent
  )

interface AccountFormProviderProps {
  children: React.ReactNode
}

export const AccountFormProvider: React.FC<AccountFormProviderProps> = ({
  children
}) => {
  const [query] = useSearchParams()
  const {
    account,
    loading: loadingAccount,
    updateAccountState,
    setLoading,
    getAccount
  } = useAccount()
  const { person, loading: loadingPerson, getPerson } = usePerson()
  const { group, loading: loadingGroup, getGroup } = useGroup()

  const [people, setPeople] = useState<Person[]>([])
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [groups, setGroups] = useState<Group[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)

  function listPeople(search: string) {
    const searchBy = ['document', 'name']

    Promise.resolve()
      .then(() => setLoadingPeople(true))
      .then(() => apiListPeople({ search, searchBy }))
      .then(data => setPeople(data.people))
      .finally(() => setLoadingPeople(false))
  }

  function listGroups(search: string) {
    Promise.resolve()
      .then(() => setLoadingGroups(true))
      .then(() => apiListGroups({ search }))
      .then(data => setGroups(data.groups))
      .finally(() => setLoadingGroups(false))
  }

  function listCategories() {
    Promise.resolve()
      .then(() => setLoadingCategories(true))
      .then(() => apiListCategories())
      .then(data => setCategories(data.categories))
      .finally(() => setLoadingCategories(false))
  }

  useEffect(() => {
    const accountId = query.get('id')

    if (!accountId) return

    getAccount(accountId)
  }, [])

  useEffect(() => {
    if (!account) return

    getPerson(account.personId)
  }, [account])

  useEffect(() => {
    if (!account?.groupId) return

    getGroup(account.groupId)
  }, [account])

  function getAccountState() {
    if (!account) throw new Error('Nenhuma conta foi selecionada')

    return account
  }

  const loading = loadingAccount || loadingPerson || loadingGroup

  return (
    <AccountFormContext.Provider
      value={{
        account,
        person,
        group,
        loading,
        getAccountState,
        updateAccountState,
        setLoading,
        people,
        loadingPeople,
        listPeople,
        groups,
        loadingGroups,
        listGroups,
        categories,
        loadingCategories,
        listCategories
      }}
    >
      <DefaultLoader active={loading} />
      {children}
    </AccountFormContext.Provider>
  )
}
